import { useCallback, useEffect, useRef } from 'react'
import { fetchImageUrl } from './api'

// Fabric clones reload their source URL. Keep bytes alive until a successful
// replacement, removal or scope teardown, rather than revoking after decode.
export function useAppArtworkUrls(images, vendorToken, builderSessionId, builderDraftPublicId) {
  const urlsRef = useRef(new Map())
  const generationRef = useRef(0)
  const imagesRef = useRef(images)
  imagesRef.current = images
  const scope = JSON.stringify([vendorToken, builderSessionId, builderDraftPublicId])
  const scopeRef = useRef(scope)
  scopeRef.current = scope

  const releaseUrl = useCallback(url => {
    if (!urlsRef.current.delete(url)) return
    URL.revokeObjectURL(url)
  }, [])

  const keepUrl = useCallback((imageId, url) => {
    for (const [ownedUrl, ownerId] of urlsRef.current) {
      if (ownerId === String(imageId) && ownedUrl !== url) releaseUrl(ownedUrl)
    }
  }, [releaseUrl])

  const getAppUrl = useCallback(async (image, signal) => {
    const generation = generationRef.current
    const url = await fetchImageUrl(vendorToken, builderSessionId, image.id, builderDraftPublicId, signal, 'app')
    const current = imagesRef.current?.some(candidate => (
      String(candidate.id) === String(image.id) &&
      Number(candidate.backgroundRevision ?? 0) === Number(image.backgroundRevision ?? 0)
    ))
    if (signal?.aborted || generationRef.current !== generation || scopeRef.current !== scope || !current) {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url)
      throw new DOMException('Artwork loading cancelled', 'AbortError')
    }
    if (url.startsWith('blob:')) urlsRef.current.set(url, String(image.id))
    return url
  }, [builderDraftPublicId, builderSessionId, scope, vendorToken])

  useEffect(() => {
    const owned = urlsRef.current
    return () => {
      generationRef.current += 1
      for (const url of owned.keys()) URL.revokeObjectURL(url)
      owned.clear()
    }
  }, [scope])

  useEffect(() => {
    const ids = new Set((images || []).map(image => String(image.id)))
    for (const [url, imageId] of urlsRef.current) {
      if (!ids.has(imageId)) releaseUrl(url)
    }
  }, [images, releaseUrl])

  return { getAppUrl, keepUrl, releaseUrl }
}
